import React, { useEffect } from "react";
import { useForm, useWatch, FormProvider, Controller } from "react-hook-form";
import { defineMessages } from "react-intl";
import { Box, Grid } from "@mui/material";
import equal from "fast-deep-equal";
import { makeStyles } from "tss-react/mui";
import { OutlinedButton, Button } from "../../button/Buttons.js";
import "../../button/EditButton.js";
import "@mui/material/Popover";
import "../../../localization/MessageLocalizer.js";
import "../../../localization/LocaleProvider.js";
import "../../../localization/sharedResources.js";
import "../../dropdown/DropdownHoc.style.js";
import { DropdownWithChip } from "../../dropdown/DropdownWithChip.js";
import "../../dropdown/DropdownItem.js";
import { useValueRangeValidation, useSelectBoxValue } from "./hooks.js";
import { OperatorSelect } from "./OperatorSelect.js";
import { RelativeRange } from "./RelativeRange.js";
import { defaultValueRangeModel } from "./types.js";
const localized = defineMessages({
  operator: {
    id: "ValueRangeFilter_operator",
    defaultMessage: "Operator"
  },
  clearAll: {
    id: "ValueRangeFilter_clearAll",
    defaultMessage: "Clear All"
  },
  apply: {
    id: "ValueRangeFilter_apply",
    defaultMessage: "Apply"
  }
});
const useClasses = makeStyles()((theme) => ({
  valueRangeFilterContainer: {
    padding: theme.spacing(2)
  },
  valueRangeFilterClearAll: {
    width: "100%"
  },
  valueRangeFilterApply: {
    borderRadius: theme.custom.roundedButtonRadius,
    border: 0,
    width: "100%"
  },
  valueRangeFilterButtonFullWidth: {
    width: "100%",
    "& .MuiButton-startIcon": {
      marginRight: "auto"
    }
  }
}));
function ValueRangeFilter({ label, value, onChange, width = "full", min, max, hideEqualOperator }) {
  const { classes, cx } = useClasses();
  const methods = useForm({ defaultValues: defaultValueRangeModel });
  const { reset, control, handleSubmit } = methods;
  const watched = useWatch({ control });
  const showToValue = ["less", "between"].includes(watched.operator);
  const showFromValue = ["greater", "between", "equals"].includes(watched.operator);
  const validate = useValueRangeValidation({
    operator: watched.operator,
    min,
    max
  });
  const parsedValue = {
    operator: getOperator(value),
    value: {
      from: value?.from,
      to: value?.to
    }
  };
  const selectedText = useSelectBoxValue(parsedValue);
  useEffect(() => {
    if (!equal(watched, parsedValue)) {
      reset(parsedValue);
    }
  }, [value?.from, value?.to]);
  useEffect(() => {
    if (parsedValue.operator !== watched.operator) {
      const { value: value2 } = defaultValueRangeModel;
      reset({
        operator: watched.operator,
        value: value2
      });
    }
  }, [watched.operator]);
  function getOperator(value2) {
    let res;
    if (!isNaN(value2?.from) && !isNaN(value2?.to)) {
      if (value2?.from === value2?.to) {
        res = "equals";
      } else {
        res = "between";
      }
    } else if (!isNaN(value2?.from) && isNaN(value2?.to)) {
      res = "greater";
    } else {
      res = "less";
    }
    return res;
  }
  function onSubmit(data) {
    onChange({
      from: data?.value?.from,
      to: data?.operator === "equals" ? data?.value?.from : data?.value?.to
    });
  }
  function handleClear() {
    methods.reset(defaultValueRangeModel);
    const { value: value2 } = defaultValueRangeModel;
    onChange(value2);
  }
  return /* @__PURE__ */ React.createElement(
    DropdownWithChip,
    {
      label,
      selectedText,
      styles: {
        dropdownButton: cx(width === "full" && classes.valueRangeFilterButtonFullWidth),
        dropdownContainer: classes.valueRangeFilterContainer
      }
    },
    /* @__PURE__ */ React.createElement(FormProvider, { ...methods }, /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(
      Controller,
      {
        render: ({ field, fieldState }) => /* @__PURE__ */ React.createElement(
          OperatorSelect,
          {
            label: localized.operator,
            value: field.value,
            onChange: field.onChange,
            key: field.name,
            fieldState,
            hideEqualOperator
          }
        ),
        control,
        name: "operator"
      }
    ), /* @__PURE__ */ React.createElement(
      Controller,
      {
        render: ({ field, fieldState }) => /* @__PURE__ */ React.createElement(
          RelativeRange,
          {
            showTo: showToValue,
            showFrom: showFromValue,
            value: field.value,
            fieldState,
            onChange: field.onChange,
            label
          }
        ),
        control,
        name: "value",
        rules: { validate }
      }
    ), /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 1 }, /* @__PURE__ */ React.createElement(Grid, { size: { xs: 12, md: 6 } }, /* @__PURE__ */ React.createElement(OutlinedButton, { className: classes.valueRangeFilterClearAll, label: localized.clearAll, onClick: handleClear })), /* @__PURE__ */ React.createElement(Grid, { size: { xs: 12, md: 6 } }, /* @__PURE__ */ React.createElement(
      Button,
      {
        onClick: handleSubmit(onSubmit),
        className: classes.valueRangeFilterApply,
        color: "primary",
        label: localized.apply
      }
    )))))
  );
}
export {
  ValueRangeFilter
};
//# sourceMappingURL=ValueRangeFilter.js.map
